/**
 * Thread REST transport. Application reader contracts live in the threads module.
 */

import {
  CommentModerationContext,
  CreateThread,
  CreateThreadComment,
  ModuleTypeEnum,
  PaginatedResponse,
  Thread,
  ThreadComment,
  ThreadCommentChangesResponse,
  ThreadCommentHistory,
  ThreadCommentReaction,
  ThreadCommentVisibilityEnum,
  ThreadCreationResult,
  ThreadQueryParams,
  ThreadReaction,
  ThreadTypeEnum,
  UpdateThreadInput,
} from '@scrambl/shared'

import { BaseApi } from './base-api'
import { getCurrentClientPortalContext } from './client-portal-context'
import { clientPortalApi } from './client-portal.api'
import { createSingletonApi } from './singleton-factory'

export interface ThreadReadRequestOptions {
  signal?: AbortSignal
  /** Omitted for legacy callers; readers bind the route before requesting. */
  portalEntityId?: string | null
}

class ThreadApiService extends BaseApi<
  Thread,
  CreateThread,
  UpdateThreadInput
> {
  constructor() {
    super({
      baseEndpoint: '/threads',
      responseNormalization: true,
    })
  }

  private getClientPortalEntityId(): string | null {
    return getCurrentClientPortalContext()?.entityId ?? null
  }

  // Override list to handle ThreadQueryParams
  async getThreads(
    params?: ThreadQueryParams,
    options: ThreadReadRequestOptions = {},
  ): Promise<PaginatedResponse<Thread>> {
    return this.makeRequest<PaginatedResponse<Thread>>(
      `${this.config.baseEndpoint}${this.buildQueryParams(params || {})}`,
      { signal: options.signal },
    )
  }

  async getThread(
    id: string,
    options: ThreadReadRequestOptions = {},
  ): Promise<Thread> {
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId) {
      const response = await clientPortalApi.getThread(
        portalEntityId,
        id,
        undefined,
        false,
        { signal: options.signal },
      )
      return response.thread as unknown as Thread
    }
    const response = await this.makeRequest<Thread | { data: Thread }>(
      `${this.config.baseEndpoint}/${id}`,
      { signal: options.signal },
    )
    return this.normalizeResponse(response)
  }

  async createThread(
    data: CreateThread,
    userId?: string,
    options: ThreadReadRequestOptions = {},
  ): Promise<ThreadCreationResult> {
    options.signal?.throwIfAborted()
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId)
      throw new Error(
        'Discussion creation is unavailable in this client context',
      )
    const body = userId ? { ...data, userId } : data
    return this.normalizeResponse(
      await this.makeRequest<ThreadCreationResult>(this.config.baseEndpoint, {
        method: 'POST',
        body,
        signal: options.signal,
      }),
    )
  }

  async updateThread(
    id: string,
    data: UpdateThreadInput,
    options: ThreadReadRequestOptions = {},
  ): Promise<Thread> {
    options.signal?.throwIfAborted()
    const portal =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portal)
      throw new Error(
        'Thread settings are unavailable in client portal context',
      )
    return this.normalizeResponse(
      await this.makeRequest<Thread>(`${this.config.baseEndpoint}/${id}`, {
        method: 'PUT',
        body: data,
        signal: options.signal,
      }),
    )
  }

  async deleteThread(
    id: string,
    options: ThreadReadRequestOptions = {},
  ): Promise<void> {
    options.signal?.throwIfAborted()
    const portal =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portal)
      throw new Error('Thread deletion is unavailable in client portal context')
    await this.makeRequest<void>(`${this.config.baseEndpoint}/${id}`, {
      method: 'DELETE',
      signal: options.signal,
    })
  }

  async toggleThreadReaction(
    data: {
      threadId: string
      emoji: string
    },
    options: ThreadReadRequestOptions = {},
  ): Promise<{ reaction: ThreadReaction | null; added: boolean }> {
    options.signal?.throwIfAborted()
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId) {
      return clientPortalApi.toggleThreadReaction(
        portalEntityId,
        data.threadId,
        data.emoji,
        { signal: options.signal },
      )
    }

    return this.makeRequest<{
      reaction: ThreadReaction | null
      added: boolean
    }>(`${this.config.baseEndpoint}/reactions/toggle`, {
      method: 'POST',
      signal: options.signal,
      body: data,
    })
  }

  // Thread Comments
  async getThreadComments(
    params: {
      threadId: string
      parentCommentId?: string | null
      sortBy?: string
      sortOrder?: 'asc' | 'desc'
      limit?: number
      page?: number
      skipCache?: boolean
    },
    options: ThreadReadRequestOptions = {},
  ): Promise<PaginatedResponse<ThreadComment>> {
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId) {
      return clientPortalApi.getThreadComments(
        portalEntityId,
        params.threadId,
        params,
        { signal: options.signal },
      )
    }

    const queryParams = new URLSearchParams()

    if (params.parentCommentId === null) {
      queryParams.append('parentCommentId', 'null')
    } else if (params.parentCommentId !== undefined) {
      queryParams.append('parentCommentId', params.parentCommentId)
    }

    queryParams.append('sortBy', params.sortBy || 'sequence')
    queryParams.append('sortOrder', params.sortOrder || 'asc')
    queryParams.append('limit', (params.limit || 10).toString())
    queryParams.append('page', (params.page || 1).toString())

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest<PaginatedResponse<ThreadComment>>(
      `${this.config.baseEndpoint}/${params.threadId}/comments${queryString}`,
      { signal: options.signal },
    )
  }

  async getThreadCommentChanges(params: {
    threadId: string
    sinceSequence?: number
    sinceUpdatedAt?: string
    limit?: number
  }): Promise<ThreadCommentChangesResponse> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.getThreadCommentChanges(
        portalEntityId,
        params.threadId,
        params,
      )
    }

    const queryParams = new URLSearchParams()

    if (typeof params.sinceSequence === 'number') {
      queryParams.append('sinceSequence', params.sinceSequence.toString())
    }
    if (params.sinceUpdatedAt) {
      queryParams.append('sinceUpdatedAt', params.sinceUpdatedAt)
    }
    if (typeof params.limit === 'number') {
      queryParams.append('limit', params.limit.toString())
    }

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    return this.makeRequest<ThreadCommentChangesResponse>(
      `${this.config.baseEndpoint}/${params.threadId}/comments/changes${queryString}`,
    )
  }

  async getThreadCommentById(commentId: string): Promise<ThreadComment> {
    return this.makeRequest<ThreadComment>(
      `${this.config.baseEndpoint}/comments/${commentId}`,
    )
  }

  async getThreadCommentHistory(
    commentId: string,
  ): Promise<ThreadCommentHistory[]> {
    return this.makeRequest<ThreadCommentHistory[]>(
      `${this.config.baseEndpoint}/comments/${commentId}/history`,
    )
  }

  async createThreadComment(
    data: CreateThreadComment & { moduleType?: string; moduleId?: string },
    options: ThreadReadRequestOptions = {},
  ): Promise<ThreadComment> {
    // Strip out moduleType and moduleId - these are only used for client-side cache updates
    const apiData: CreateThreadComment & {
      moduleType?: string
      moduleId?: string
    } = { ...data }
    delete apiData.moduleType
    delete apiData.moduleId

    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId) {
      return clientPortalApi.createThreadComment(
        portalEntityId,
        apiData.threadId,
        apiData as CreateThreadComment,
        { signal: options.signal },
      ) as unknown as Promise<ThreadComment>
    }

    return this.makeRequest<ThreadComment>(
      `${this.config.baseEndpoint}/comments`,
      {
        method: 'POST',
        body: apiData,
        signal: options.signal,
      },
    )
  }

  async updateThreadComment(
    id: string,
    content: string,
    threadId?: string,
    options: ThreadReadRequestOptions = {},
  ): Promise<ThreadComment> {
    options.signal?.throwIfAborted()
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId) {
      if (!threadId) {
        throw new Error('Thread ID is required when updating portal comments')
      }
      return clientPortalApi.updateThreadComment(
        portalEntityId,
        threadId,
        id,
        content,
        { signal: options.signal },
      )
    }

    return this.makeRequest<ThreadComment>(
      `${this.config.baseEndpoint}/comments/${id}`,
      {
        method: 'PUT',
        body: { content },
        signal: options.signal,
      },
    )
  }

  async deleteThreadComment(
    id: string,
    threadId?: string,
    options: ThreadReadRequestOptions = {},
  ): Promise<void> {
    options.signal?.throwIfAborted()
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId) {
      if (!threadId) {
        throw new Error('Thread ID is required when deleting portal comments')
      }
      await clientPortalApi.deleteComment(portalEntityId, threadId, id, {
        signal: options.signal,
      })
      return
    }

    await this.makeRequest<void>(`${this.config.baseEndpoint}/comments/${id}`, {
      method: 'DELETE',
      signal: options.signal,
    })
  }

  // The org's comment moderation policy for the current viewer (portal
  // viewers get the client-facing policy, which never bypasses).
  async getCommentModerationPolicy(): Promise<CommentModerationContext> {
    const portalEntityId = this.getClientPortalEntityId()
    if (portalEntityId) {
      return clientPortalApi.getCommentModerationPolicy(portalEntityId)
    }
    return this.makeRequest<CommentModerationContext>(
      `${this.config.baseEndpoint}/comment-policy`,
    )
  }

  // Staff-only: change a root comment's public/internal visibility. Portal
  // users never invoke this (they can't see the toggle and comments are forced
  // public server-side).
  async updateThreadCommentVisibility(
    id: string,
    visibility: ThreadCommentVisibilityEnum,
    options: ThreadReadRequestOptions = {},
  ): Promise<ThreadComment> {
    if (
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    ) {
      throw new Error(
        'Comment visibility cannot be changed from the client portal',
      )
    }
    return this.makeRequest<ThreadComment>(
      `${this.config.baseEndpoint}/comments/${id}/visibility`,
      {
        method: 'PATCH',
        body: { visibility },
        signal: options.signal,
      },
    )
  }

  /** Resolve or reopen a root note comment. */
  async setThreadCommentResolution(
    id: string,
    resolved: boolean,
    options: ThreadReadRequestOptions = {},
  ): Promise<ThreadComment> {
    options.signal?.throwIfAborted()
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId) {
      throw new Error('Comments cannot be resolved from the client portal')
    }
    return this.makeRequest<ThreadComment>(
      `${this.config.baseEndpoint}/comments/${id}/resolution`,
      {
        method: 'PATCH',
        body: { resolved },
        signal: options.signal,
      },
    )
  }

  // Comment Reactions
  async toggleThreadCommentReaction(
    data: {
      commentId: string
      emoji: string
      threadId?: string
    },
    options: ThreadReadRequestOptions = {},
  ): Promise<{ reaction: ThreadCommentReaction | null; added: boolean }> {
    options.signal?.throwIfAborted()
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId) {
      if (!data.threadId) {
        throw new Error(
          'Thread ID is required when toggling portal comment reactions',
        )
      }
      return clientPortalApi.toggleCommentReaction(
        portalEntityId,
        data.threadId,
        data.commentId,
        data.emoji,
        { signal: options.signal },
      )
    }

    const { commentId, emoji } = data
    return this.makeRequest<{
      reaction: ThreadCommentReaction | null
      added: boolean
    }>(`${this.config.baseEndpoint}/comments/reactions/toggle`, {
      method: 'POST',
      signal: options.signal,
      body: { commentId, emoji },
    })
  }

  async getThreadIdByCommentId(commentId: string): Promise<string> {
    const response = await this.makeRequest<{ threadId: string }>(
      `${this.config.baseEndpoint}/comments/${commentId}/thread`,
    )
    return response.threadId
  }

  // Module thread methods
  async getOrCreateModuleThread(params: {
    moduleType: ModuleTypeEnum
    moduleId: string
    moduleName?: string
  }): Promise<Thread> {
    const existingThreadsResponse = await this.getThreads({
      moduleType: params.moduleType,
      moduleId: params.moduleId,
      threadType: ThreadTypeEnum.COMMENT,
      limit: 1,
    })

    if (existingThreadsResponse.data.length > 0) {
      return existingThreadsResponse.data[0]
    }

    const threadTitle = params.moduleName
      ? `Comments for ${params.moduleName}`
      : `Discussion`

    return this.createThread({
      title: threadTitle,
      description: `Comments and discussion for this ${params.moduleType.toLowerCase()}.`,
      threadType: ThreadTypeEnum.COMMENT,
      moduleType: params.moduleType,
      moduleId: params.moduleId,
    })
  }

  async getOrCreateModuleThreadWithComments(
    params: {
      moduleType: ModuleTypeEnum
      moduleId: string
      moduleName?: string
      commentsLimit?: number
    },
    options: { signal?: AbortSignal } = {},
  ): Promise<{
    thread: Thread
    comments: ThreadComment[]
    totalComments: number
  }> {
    const queryParams = new URLSearchParams()
    if (params.moduleName) queryParams.append('moduleName', params.moduleName)
    if (params.commentsLimit)
      queryParams.append('commentsLimit', params.commentsLimit.toString())

    const queryString = queryParams.toString()
      ? `?${queryParams.toString()}`
      : ''
    const endpoint = `${this.config.baseEndpoint}/module/${params.moduleType}/${params.moduleId}/with-comments${queryString}`

    // The shared HTTP client already deduplicates exact requests by account,
    // workspace, relationship and URL. A module-only map cannot own responses.
    return this.makeRequest<{
      thread: Thread
      comments: ThreadComment[]
      totalComments: number
    }>(endpoint, { signal: options.signal })
  }

  clearCache(): void {
    // No-op - TanStack Query handles caching now
  }

  /**
   * Mark a thread as read for the current user.
   * Updates the lastReadAt timestamp and marks related notifications as read.
   */
  async markThreadAsRead(
    threadId: string,
    options: ThreadReadRequestOptions = {},
  ): Promise<{ success: boolean }> {
    options.signal?.throwIfAborted()
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId)
      throw new Error(
        'Portal discussion read markers require a loaded comment page',
      )
    return this.makeRequest<{ success: boolean }>(
      `${this.config.baseEndpoint}/${threadId}/read`,
      {
        method: 'POST',
        signal: options.signal,
      },
    )
  }

  /**
   * Get count of unread threads for the current user.
   * Efficient endpoint that counts in the database instead of fetching all threads.
   * @param threadType - Optional filter to count only specific thread types (e.g., DIRECT_MESSAGE, DISCUSSION)
   */
  async getUnreadCount(
    threadType?: string,
    options: ThreadReadRequestOptions = {},
  ): Promise<{ count: number }> {
    const queryParams = threadType
      ? `?threadType=${encodeURIComponent(threadType)}`
      : ''
    return this.makeRequest<{ count: number }>(
      `${this.config.baseEndpoint}/stats/unread-count${queryParams}`,
      { signal: options.signal },
    )
  }

  /**
   * Mark all threads as read for the current user.
   * Returns the number of threads marked as read.
   */
  async markAllAsRead(
    options: ThreadReadRequestOptions = {},
  ): Promise<{ success: boolean; count: number }> {
    options.signal?.throwIfAborted()
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId)
      throw new Error('Mark all read is unavailable in client portal context')
    return this.makeRequest<{ success: boolean; count: number }>(
      `${this.config.baseEndpoint}/mark-all-read`,
      {
        method: 'POST',
        signal: options.signal,
      },
    )
  }

  /**
   * Mark a thread as unread for the current user.
   * Sets the lastReadAt to null to force unread status.
   */
  async markThreadAsUnread(
    threadId: string,
    options: ThreadReadRequestOptions = {},
  ): Promise<{ success: boolean }> {
    options.signal?.throwIfAborted()
    const portalEntityId =
      options.portalEntityId === undefined
        ? this.getClientPortalEntityId()
        : options.portalEntityId
    if (portalEntityId)
      throw new Error('Mark unread is unavailable in client portal context')
    return this.makeRequest<{ success: boolean }>(
      `${this.config.baseEndpoint}/${threadId}/unread`,
      {
        method: 'POST',
        signal: options.signal,
      },
    )
  }
}

// Export singleton instance
const threadApiInstance = createSingletonApi(ThreadApiService)

// Export object-style API for backward compatibility
export const threadApi = {
  clearCache: () => threadApiInstance.clearCache(),
  getThreads: (
    params?: ThreadQueryParams,
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.getThreads(params, options),
  getThread: (id: string, options?: ThreadReadRequestOptions) =>
    threadApiInstance.getThread(id, options),
  createThread: (
    data: CreateThread,
    userId?: string,
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.createThread(data, userId, options),
  updateThread: (
    id: string,
    data: UpdateThreadInput,
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.updateThread(id, data, options),
  deleteThread: (id: string, options?: ThreadReadRequestOptions) =>
    threadApiInstance.deleteThread(id, options),
  toggleThreadReaction: (
    data: { threadId: string; emoji: string },
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.toggleThreadReaction(data, options),
  getThreadComments: (
    params: Parameters<typeof threadApiInstance.getThreadComments>[0],
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.getThreadComments(params, options),
  getThreadCommentChanges: (
    params: Parameters<typeof threadApiInstance.getThreadCommentChanges>[0],
  ) => threadApiInstance.getThreadCommentChanges(params),
  getThreadCommentById: (commentId: string) =>
    threadApiInstance.getThreadCommentById(commentId),
  getThreadCommentHistory: (commentId: string) =>
    threadApiInstance.getThreadCommentHistory(commentId),
  createThreadComment: (
    data: CreateThreadComment & { moduleType?: string; moduleId?: string },
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.createThreadComment(data, options),
  updateThreadComment: (
    id: string,
    content: string,
    threadId?: string,
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.updateThreadComment(id, content, threadId, options),
  updateThreadCommentVisibility: (
    id: string,
    visibility: ThreadCommentVisibilityEnum,
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.updateThreadCommentVisibility(id, visibility, options),
  deleteThreadComment: (
    id: string,
    threadId?: string,
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.deleteThreadComment(id, threadId, options),
  getCommentModerationPolicy: () =>
    threadApiInstance.getCommentModerationPolicy(),
  setThreadCommentResolution: (
    id: string,
    resolved: boolean,
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.setThreadCommentResolution(id, resolved, options),
  toggleThreadCommentReaction: (
    data: {
      commentId: string
      emoji: string
      threadId?: string
    },
    options?: ThreadReadRequestOptions,
  ) => threadApiInstance.toggleThreadCommentReaction(data, options),
  getThreadIdByCommentId: (commentId: string) =>
    threadApiInstance.getThreadIdByCommentId(commentId),
  getOrCreateModuleThread: (
    params: Parameters<typeof threadApiInstance.getOrCreateModuleThread>[0],
  ) => threadApiInstance.getOrCreateModuleThread(params),
  getOrCreateModuleThreadWithComments: (
    params: Parameters<
      typeof threadApiInstance.getOrCreateModuleThreadWithComments
    >[0],
    options?: { signal?: AbortSignal },
  ) => threadApiInstance.getOrCreateModuleThreadWithComments(params, options),
  markThreadAsRead: (threadId: string, options?: ThreadReadRequestOptions) =>
    threadApiInstance.markThreadAsRead(threadId, options),
  getUnreadCount: (threadType?: string, options?: ThreadReadRequestOptions) =>
    threadApiInstance.getUnreadCount(threadType, options),
  markAllAsRead: (options?: ThreadReadRequestOptions) =>
    threadApiInstance.markAllAsRead(options),
  markThreadAsUnread: (threadId: string, options?: ThreadReadRequestOptions) =>
    threadApiInstance.markThreadAsUnread(threadId, options),
}

// Export class instance
export { threadApiInstance as threadApiService }
